import { fetchTimeline } from '../../../../services/timeline';
import {
    getDxPatientFileId,
    buildFileName
} from '../../../patient-files/actions/dx-report/landing/utils';
import type { TimelineEvent } from '../../../patient-files/actions/types';
import type { DxReportOrthopulseStatus } from '../../../patient-files/actions/dx-report/types';
import {
    sortEventsLatestFirst,
    TIMELINE_VERSION_FETCH_LIMIT
} from '../review-surface/timelineVersionUtils';
import type { DocumentVersion } from '../review-surface/VersionedPdfPane';

export interface DxReportApprovalData {
    /** Latest first — mirrors the dashboard's fetchDxReportVersions ordering. */
    versions: DocumentVersion[];
    orthopulseStatus: DxReportOrthopulseStatus | null;
    /** True when the raw fetch hit TIMELINE_VERSION_FETCH_LIMIT - older versions may be hidden. */
    hasMoreVersions: boolean;
}

function extractOrthopulseStatus(
    event: TimelineEvent | undefined
): DxReportOrthopulseStatus | null {
    const formData = event?.payload?.formData as Record<string, unknown> | undefined;
    const status = formData?.orthopulseStatus;
    return status === 'mandatory' || status === 'recommended' || status === 'not_needed'
        ? status
        : null;
}

// Dx report versions + the orthopulse recommendation off the latest one, from
// a single dx_report_submission timeline fetch (versions latest-first).
export async function fetchDxReportApprovalData(patientId: string): Promise<DxReportApprovalData> {
    const { data } = await fetchTimeline(patientId, {
        type: 'dx_report_submission',
        limit: TIMELINE_VERSION_FETCH_LIMIT
    });
    const active = sortEventsLatestFirst(data.filter((e) => e.status === 'active'));
    const versions = active
        .map((event) => {
            const patientFileId = getDxPatientFileId(event);
            if (!patientFileId) return null;
            return {
                id: event.id,
                version: event.version,
                createdAt: event.createdAt,
                patientFileId,
                fileName: buildFileName(event)
            };
        })
        .filter((v): v is DocumentVersion => v !== null);
    return {
        versions,
        orthopulseStatus: extractOrthopulseStatus(active[0]),
        hasMoreVersions: data.length >= TIMELINE_VERSION_FETCH_LIMIT
    };
}
